import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContentProtection } from '../../hooks/useContentProtection';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import {
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  BookOpen,
  CheckCircle,
  AlertCircle,
  Mic,
  RotateCcw,
  Home
} from 'lucide-react';

const ARCChapterReader = () => {
  const { chapterId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Enable content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [chapter, setChapter] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  
  const audioRef = useRef(null);

  const fetchChapter = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/arc/chapter/${chapterId}`);
      setChapter(response.data.chapter);
    } catch (error) {
      console.error('Failed to fetch chapter:', error);
      toast({
        title: 'Error',
        description: 'Failed to load chapter',
        variant: 'destructive'
      });
      navigate('/arc');
    } finally {
      setLoading(false);
    }
  }, [api, chapterId, navigate, toast]);

  useEffect(() => {
    fetchChapter();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [fetchChapter]);

  const currentPage = chapter?.pages[currentPageIndex];

  const playAudio = async () => {
    if (!currentPage) return;
    
    try {
      setAudioLoading(true);
      
      // Stop any existing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      const response = await api.post(`/arc/chapter/${chapterId}/page/${currentPage.id}/tts`);
      const audioBase64 = response.data.audio_base64;
      
      const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
      audioRef.current = audio;
      
      audio.addEventListener('play', () => setIsPlaying(true));
      audio.addEventListener('pause', () => setIsPlaying(false));
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setAudioProgress(0);
      });
      audio.addEventListener('timeupdate', () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100);
        }
      });
      
      await audio.play();
    } catch (error) {
      console.error('Audio playback error:', error);
      toast({
        title: 'Audio Error',
        description: 'Failed to play audio. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setAudioLoading(false);
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) {
      playAudio();
      return;
    }
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setAudioProgress(0);
    }
  };

  const goToPage = (index) => {
    stopAudio();
    setCurrentPageIndex(index);
  };

  const goToNextPage = () => {
    if (currentPageIndex < chapter.pages.length - 1) {
      goToPage(currentPageIndex + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPageIndex > 0) {
      goToPage(currentPageIndex - 1);
    }
  };

  const startQuiz = () => {
    stopAudio();
    navigate(`/arc/chapter/${chapterId}/quiz`);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[60vh]" />
        </div>
      </MainLayout>
    );
  }

  if (!chapter) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Chapter Not Found</h2>
            <Button onClick={() => navigate('/arc')}>Return to Dashboard</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const isLastPage = currentPageIndex === chapter.pages.length - 1;

  return (
    <MainLayout>
      {/* Anti-piracy notice */}
      <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-4 py-2">
        <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
          This content is protected. Copying, downloading, or screenshots are disabled.
        </p>
      </div>
      
      <div 
        className="p-6 space-y-6 select-none"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/arc')}>
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <Badge variant="outline" className="mb-1">
                {chapter.module_title}
              </Badge>
              <h1 className="text-xl font-bold">
                Chapter {chapter.number}: {chapter.title}
              </h1>
            </div>
          </div>
          <Badge className="bg-amber-500">
            Page {currentPageIndex + 1} of {chapter.total_pages}
          </Badge>
        </div>

        {/* Progress Bar */}
        <Progress 
          value={((currentPageIndex + 1) / chapter.total_pages) * 100} 
          className="h-2"
        />

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Page Navigation Sidebar */}
          <div className="hidden lg:block space-y-2">
            <h3 className="font-semibold text-sm mb-3">Pages</h3>
            {chapter.pages.map((page, index) => (
              <button
                key={page.id}
                onClick={() => goToPage(index)}
                className={`w-full text-left p-2 rounded-lg text-sm transition-all ${
                  index === currentPageIndex
                    ? 'bg-amber-100 dark:bg-amber-900/50 border-l-4 border-amber-500'
                    : 'hover:bg-muted'
                }`}
              >
                <span className="font-medium">Page {page.number}</span>
                <p className="text-xs text-muted-foreground truncate">{page.title}</p>
              </button>
            ))}
            
            {chapter.has_quiz && (
              <div className="pt-4 border-t">
                <Button 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                  onClick={startQuiz}
                  disabled={currentPageIndex < chapter.pages.length - 1}
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Take Quiz
                </Button>
                {currentPageIndex < chapter.pages.length - 1 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Complete all pages first
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <Card className="min-h-[60vh]">
              <CardContent className="p-8">
                {/* Audio Controls */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-lg">
                  <Button
                    size="lg"
                    variant={isPlaying ? "secondary" : "default"}
                    onClick={toggleAudio}
                    disabled={audioLoading}
                    className={!isPlaying ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' : ''}
                  >
                    {audioLoading ? (
                      <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full" />
                    ) : isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </Button>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Volume2 className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium">
                        {isPlaying ? 'Playing...' : 'Listen to this page'}
                      </span>
                    </div>
                    <Progress value={audioProgress} className="h-1" />
                  </div>
                  
                  {isPlaying && (
                    <Button variant="ghost" size="sm" onClick={stopAudio}>
                      <VolumeX className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Page Title */}
                <h2 className="text-2xl font-bold mb-6 text-amber-900 dark:text-amber-100">
                  {currentPage.title}
                </h2>

                {/* Page Content */}
                <div 
                  className="prose prose-lg dark:prose-invert max-w-none mb-8"
                  style={{ userSelect: 'none' }}
                >
                  {currentPage.content.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="mb-4 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Key Points */}
                {currentPage.key_points && currentPage.key_points.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-6 mt-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-amber-600" />
                      Key Takeaways
                    </h3>
                    <ul className="space-y-2">
                      {currentPage.key_points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-amber-600 mt-1 flex-shrink-0" />
                          <span className="text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={goToPrevPage}
                    disabled={currentPageIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  {isLastPage ? (
                    chapter.has_quiz ? (
                      <Button 
                        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                        onClick={startQuiz}
                      >
                        <Mic className="h-4 w-4 mr-2" />
                        Start Chapter Quiz
                      </Button>
                    ) : (
                      <Button onClick={() => navigate('/arc')}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Complete Chapter
                      </Button>
                    )
                  ) : (
                    <Button onClick={goToNextPage}>
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Mobile Page Navigation */}
            <div className="lg:hidden mt-4 flex overflow-x-auto gap-2 pb-2">
              {chapter.pages.map((page, index) => (
                <button
                  key={page.id}
                  onClick={() => goToPage(index)}
                  className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs ${
                    index === currentPageIndex
                      ? 'bg-amber-500 text-white'
                      : 'bg-muted hover:bg-amber-100'
                  }`}
                >
                  {page.number}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ARCChapterReader;
